import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DateInput } from "react-admin";
import { formatListDate } from "@/utils/date-format";
import { fromGregorian } from "@/utils/hijri-date-utils";

export type ZabihatBookedSlotFormRow = {
  bookedDate?: string;
  performanceMode?: "SELF" | "VAKALAT";
};

const MODE_CHOICES = [
  { id: "SELF", name: "Self" },
  { id: "VAKALAT", name: "Vakalat" },
] as const;

function parseIsoDate(value: string | undefined): Date | null {
  if (!value || typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function SlotRowLabel({ bookedDate }: { bookedDate?: string }) {
  const d = parseIsoDate(bookedDate);
  if (!d) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
      {formatListDate(d, { empty: "—" })} · {fromGregorian(d, "long")}
    </Typography>
  );
}

/**
 * Optional zabihat day assignments for ZABIHAT contributions.
 * Form field: `bookedSlots: { bookedDate, performanceMode }[]`
 */
export default function FmbZabihatBookedSlotsInput() {
  const { control, setValue } = useFormContext();
  const contributionType = useWatch({ control, name: "contributionType" });
  const quantity = Number(useWatch({ control, name: "quantity" })) || 0;
  const bookedSlots =
    (useWatch({ control, name: "bookedSlots" }) as ZabihatBookedSlotFormRow[] | undefined) || [];

  if (contributionType !== "ZABIHAT") {
    return null;
  }

  const assigned = bookedSlots.length;
  const canAdd = assigned < quantity;

  const updateSlots = (next: ZabihatBookedSlotFormRow[]) => {
    setValue("bookedSlots", next, { shouldDirty: true, shouldTouch: true });
  };

  const handleAdd = () => {
    if (!canAdd) return;
    updateSlots([...bookedSlots, { bookedDate: undefined, performanceMode: "SELF" }]);
  };

  const handleRemove = (index: number) => {
    updateSlots(bookedSlots.filter((_, i) => i !== index));
  };

  const handleModeChange = (index: number, mode: "SELF" | "VAKALAT") => {
    updateSlots(
      bookedSlots.map((row, i) => (i === index ? { ...row, performanceMode: mode } : row))
    );
  };

  return (
    <Box sx={{ width: 1, mt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">
          Zabihat dates (optional) — assigned {assigned} of {quantity || 0}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canAdd}
          variant="outlined"
        >
          Add date
        </Button>
      </Box>
      {quantity > 0 && assigned > quantity ? (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Too many dates for quantity {quantity}. Remove {assigned - quantity} before saving.
        </Typography>
      ) : null}
      {bookedSlots.map((row, index) => (
        <Box
          key={`zabihat-slot-${index}`}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "flex-start",
            mb: 1.5,
            p: 1.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
            <DateInput
              source={`bookedSlots.${index}.bookedDate`}
              label={`Date ${index + 1}`}
              fullWidth
              helperText={false}
            />
            <SlotRowLabel bookedDate={row.bookedDate} />
          </Box>
          <FormControl size="small" sx={{ minWidth: 140, mt: 1 }}>
            <InputLabel id={`zabihat-mode-${index}`}>Mode</InputLabel>
            <Select
              labelId={`zabihat-mode-${index}`}
              label="Mode"
              value={row.performanceMode ?? "SELF"}
              onChange={(e) => handleModeChange(index, e.target.value as "SELF" | "VAKALAT")}
            >
              {MODE_CHOICES.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            aria-label={`Remove zabihat date ${index + 1}`}
            onClick={() => handleRemove(index)}
            sx={{ mt: 1 }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      {assigned === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Dates are optional. Assign up to {quantity || 0} day(s) for this contribution.
        </Typography>
      ) : null}
    </Box>
  );
}

/** Client-side: bookedSlots length must be ≤ quantity for ZABIHAT. */
export function validateZabihatBookedSlots(values: Record<string, unknown>) {
  const errors: Record<string, string> = {};
  if (values.contributionType !== "ZABIHAT") {
    return errors;
  }
  const quantity = Number(values.quantity) || 0;
  const slots = Array.isArray(values.bookedSlots) ? values.bookedSlots : [];
  if (slots.length > quantity) {
    errors.quantity = `Remove zabihat dates first (assigned ${slots.length}, quantity ${quantity})`;
    errors.bookedSlots = `Assigned dates (${slots.length}) exceed quantity (${quantity})`;
  }
  for (let i = 0; i < slots.length; i += 1) {
    const row = slots[i] as ZabihatBookedSlotFormRow;
    if (!row?.bookedDate) {
      errors[`bookedSlots.${i}.bookedDate`] = "Date is required";
    }
    if (row?.performanceMode !== "SELF" && row?.performanceMode !== "VAKALAT") {
      errors[`bookedSlots.${i}.performanceMode`] = "Select Self or Vakalat";
    }
  }
  return errors;
}

/** Normalize react-admin DateInput values to YYYY-MM-DD for the API. */
export function transformBookedSlotsForApi(
  data: Record<string, unknown>
): { bookedDate: string; performanceMode: "SELF" | "VAKALAT" }[] | undefined {
  if (data.contributionType !== "ZABIHAT") {
    return undefined;
  }
  if (!Array.isArray(data.bookedSlots)) {
    return undefined;
  }
  return (data.bookedSlots as ZabihatBookedSlotFormRow[])
    .map((row) => {
      const raw = row.bookedDate;
      let bookedDate = "";
      if (typeof raw === "string") {
        bookedDate = raw.slice(0, 10);
      } else if (raw && typeof raw === "object" && "toISOString" in (raw as object)) {
        bookedDate = (raw as Date).toISOString().slice(0, 10);
      }
      return {
        bookedDate,
        performanceMode: (row.performanceMode ?? "SELF") as "SELF" | "VAKALAT",
      };
    })
    .filter((row) => /^\d{4}-\d{2}-\d{2}$/.test(row.bookedDate));
}
